import React, {useCallback, useLayoutEffect, useMemo, useRef, useState} from 'react';

const VISIBLE_COLLAPSED = 5;
const SHOW_ALL_BTN_RESERVE = 52;
const BOTTOM_PAD = 8;
/** Full vertical padding on .sidebar-doc-item (9px top + 9px bottom); first/last rows strip one or both. */
const DOC_ITEM_FULL_VERTICAL_PAD = 18;

function effectiveDocRowHeight(item) {
  const measured = item.getBoundingClientRect().height;
  if (measured <= 0) {
    return measured;
  }
  const style = window.getComputedStyle(item);
  const padTop = parseFloat(style.paddingTop) || 0;
  const padBottom = parseFloat(style.paddingBottom) || 0;
  return measured + (DOC_ITEM_FULL_VERTICAL_PAD - padTop - padBottom);
}

const SOURCE_LABEL_KEYS = {
  ai: 'project.documentSourceAi',
  chat_upload: 'project.documentSourceClient',
  template: 'project.documentSourceTemplate',
  lawyer: 'project.documentSourceLawyer',
  client: 'project.documentSourceClient',
};

function formatDocDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = d.getDate();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

function sortFiles(files) {
  return [...files].sort((a, b) => {
    const ta = new Date(a.created_at || 0).getTime();
    const tb = new Date(b.created_at || 0).getTime();
    return tb - ta;
  });
}

function buildLawyerFileKeys(communications) {
  const keys = new Set();
  if (!Array.isArray(communications)) {
    return keys;
  }
  communications.forEach((msg) => {
    if (msg?.sender_role !== 'lawyer') {
      return;
    }
    (msg.files || []).forEach((f) => {
      const id = f?.id || f?.fileId;
      if (id) {
        keys.add(`id:${String(id)}`);
      }
      if (f?.url) {
        keys.add(`url:${String(f.url)}`);
      }
    });
  });
  return keys;
}

function resolveDocumentDisplaySource(file, lawyerFileKeys) {
  const stored = file?.source;
  if (stored === 'ai' || stored === 'template') {
    return stored;
  }
  const fileId = file?.fileId || file?.id;
  if (fileId && lawyerFileKeys.has(`id:${String(fileId)}`)) {
    return 'lawyer';
  }
  if (file?.url && lawyerFileKeys.has(`url:${String(file.url)}`)) {
    return 'lawyer';
  }
  return stored || 'chat_upload';
}

export function ProjectDocumentsList({
  files = [],
  communications = [],
  t,
  pinned = false,
  onExpandedChange,
}) {
  const [expanded, setExpanded] = useState(false);
  const [pinnedLimit, setPinnedLimit] = useState(null);
  const sectionRef = useRef(null);
  const listRef = useRef(null);

  const lawyerFileKeys = useMemo(
    () => buildLawyerFileKeys(communications),
    [communications],
  );

  const list = useMemo(
    () => sortFiles(Array.isArray(files) ? files.filter((f) => f?.url || f?.fileId) : []),
    [files],
  );
  const total = list.length;
  const title = t ? t('project.documentsSection') : 'Documents';

  const measurePinnedLimit = useCallback(() => {
    if (!pinned || expanded || total === 0) {
      return;
    }
    const section = sectionRef.current;
    const ul = listRef.current;
    const client = section?.closest('.client');
    const meta = client?.querySelector('.sidebar-meta');
    const items = ul?.querySelectorAll('.sidebar-doc-item');
    const firstItem = items?.[0];
    if (!section || !ul || !client || !firstItem) {
      return;
    }

    const clientRect = client.getBoundingClientRect();
    const clientStyle = window.getComputedStyle(client);
    const paddingBottom = parseFloat(clientStyle.paddingBottom) || 0;
    const contentBottom = clientRect.bottom - paddingBottom;

    const listTop = ul.getBoundingClientRect().top;
    const metaTop = meta ? meta.getBoundingClientRect().top : contentBottom;

    const heightItem = items.length >= 2 ? items[1] : firstItem;
    const rowHeight = effectiveDocRowHeight(heightItem);
    if (rowHeight <= 0) {
      return;
    }

    const showAllBtn = section.querySelector('.sidebar-doc-show-all');
    let btnReserve = SHOW_ALL_BTN_RESERVE;
    if (showAllBtn) {
      const btnStyle = window.getComputedStyle(showAllBtn);
      const marginTop = parseFloat(btnStyle.marginTop) || 0;
      btnReserve = showAllBtn.getBoundingClientRect().height + marginTop;
    }

    const baseAvailable = metaTop - listTop - BOTTOM_PAD;
    const withoutBtn = Math.floor(baseAvailable / rowHeight);
    let count = withoutBtn;
    if (withoutBtn < total) {
      count = Math.max(1, Math.floor((baseAvailable - btnReserve) / rowHeight));
    }
    setPinnedLimit((prev) => {
      const next = Math.min(count, total);
      return prev === next ? prev : next;
    });
  }, [pinned, expanded, total]);

  useLayoutEffect(() => {
    if (!pinned || expanded) {
      setPinnedLimit(null);
      return undefined;
    }
    measurePinnedLimit();
    const section = sectionRef.current;
    const client = section?.closest('.client');
    if (!client) {
      return undefined;
    }
    const ro = new ResizeObserver(() => {
      measurePinnedLimit();
    });
    ro.observe(client);
    window.addEventListener('resize', measurePinnedLimit);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measurePinnedLimit);
    };
  }, [pinned, expanded, list, measurePinnedLimit]);

  const collapsedLimit = pinned
    ? (pinnedLimit ?? Math.min(VISIBLE_COLLAPSED, total))
    : VISIBLE_COLLAPSED;

  const visible = expanded || total <= collapsedLimit
    ? list
    : list.slice(0, collapsedLimit);

  const showToggle = pinned
    ? (expanded ? total > collapsedLimit : total > collapsedLimit)
    : total > VISIBLE_COLLAPSED;

  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    onExpandedChange?.(next);
  };

  const getSourceLabel = (source) => {
    const key = SOURCE_LABEL_KEYS[source] || SOURCE_LABEL_KEYS.chat_upload;
    return t ? t(key) : source;
  };

  const emptyHint = t
    ? t('project.documentsEmptyHint').replace('{action}', t('project.thread.addFiles'))
    : '';

  return (
    <div ref={sectionRef} className="sidebar-section sidebar-section--documents">
      <h3 className="sidebar-section-title">{title}</h3>
      {total === 0 ? (
        <div className="sidebar-doc-empty">
          <p className="sidebar-doc-empty-title">
            {t ? t('project.documentsEmptyTitle') : 'No documents yet'}
          </p>
          <p className="sidebar-doc-empty-hint">{emptyHint}</p>
        </div>
      ) : (
        <>
          <ul ref={listRef} className="sidebar-doc-list">
            {visible.map((file) => {
              const dateStr = formatDocDate(file.created_at);
              const meta = [
                getSourceLabel(resolveDocumentDisplaySource(file, lawyerFileKeys)),
                dateStr,
              ]
                .filter(Boolean)
                .join(' • ');
              return (
                <li key={file.fileId || file.url} className="sidebar-doc-item">
                  <a href={file.url} target="_blank" rel="noopener noreferrer">
                    {file.name || file.fileId}
                  </a>
                  {meta ? <span className="sidebar-doc-meta">{meta}</span> : null}
                </li>
              );
            })}
          </ul>
          {showToggle ? (
            <button
              type="button"
              className="sidebar-doc-show-all"
              onClick={toggleExpanded}
            >
              {expanded
                ? t('project.documentsShowLess')
                : t('project.documentsShowAll').replace('{count}', String(total))}
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}
